import React, { useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import { getCatalogToolScope } from '../../../api/catalog_tool';
import './ScopeSelector.scss';

const SCOPE_OPTIONS = [
  { value: 'brand', label: 'Brand (All Brands ID)' },
  { value: 'product', label: 'Product (PID)' },
  { value: 'pin', label: 'Pin ID' }
];

/**
 * Scope step for the Catalog Tool: pill toggles + entity search + persistent confirmation card.
 * Processing stays blocked upstream until `selected` is set, so an operator always confirms
 * which entity they are working on before anything runs.
 */
const ScopeSelector = props => {
  const { mode, scopeType, onScopeTypeChange, selected, onSelect, onClear } = props;

  const [searchVal, setSearchVal] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingCard, setIsLoadingCard] = useState(false);
  const latestSearchId = useRef(0);

  const searchDebounced = useMemo(
    () =>
      AwesomeDebouncePromise(async (type, query, searchId) => {
        const response = await getCatalogToolScope({ type, query });
        return { searchId, results: response.data.results || [] };
      }, 250),
    []
  );

  const handleSearchChange = async value => {
    setSearchVal(value);
    // Every keystroke invalidates in-flight lookups immediately — incrementing inside the
    // debounced callback would leave a 250ms window where a stale response slips through.
    const searchId = ++latestSearchId.current;
    if (value.trim().length < 2) {
      setSearchResults(null);
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    try {
      const { searchId: returnedId, results } = await searchDebounced(scopeType, value.trim(), searchId);
      if (returnedId !== latestSearchId.current) return;
      setSearchResults(results);
      setIsSearching(false);
    } catch (error) {
      if (searchId !== latestSearchId.current) return;
      setIsSearching(false);
      window.ALERT.error(error || 'Search failed');
    }
  };

  const resetSearch = () => {
    latestSearchId.current++;
    setSearchVal('');
    setSearchResults(null);
    setIsSearching(false);
  };

  const selectScopeType = type => {
    if (type === scopeType) return;
    onScopeTypeChange(type);
    resetSearch();
    onClear();
  };

  const selectResult = async result => {
    resetSearch();
    // resetSearch just bumped the generation counter; a scope-pill switch while this card
    // lookup is in flight bumps it again, which voids the lookup — otherwise a stale card
    // could be confirmed under the wrong scope type.
    const capturedSearchId = latestSearchId.current;
    setIsLoadingCard(true);
    try {
      const response = await getCatalogToolScope({ type: scopeType, id: result.id });
      if (capturedSearchId !== latestSearchId.current) return;
      if (scopeType === 'brand') {
        const { brand, similar } = response.data;
        if (!brand) return window.ALERT.error('Brand no longer exists');
        onSelect({ type: 'brand', ...brand, similar });
      } else {
        const { product } = response.data;
        if (!product) return window.ALERT.error(`No product found for '${result.id}'. This tool never creates products.`);
        onSelect({ type: 'product', ...product });
      }
    } catch (error) {
      window.ALERT.error(error || 'Failed to load details');
    } finally {
      setIsLoadingCard(false);
    }
  };

  const changeSelection = () => {
    resetSearch();
    onClear();
  };

  const showNotFound = searchResults && searchResults.length === 0 && !isSearching;
  const searchPlaceholder = scopeType === 'brand' ? 'Search brands by name, domain or ID' : 'Search products by name or PID';

  return (
    <div className='catalog-scope-selector'>
      {mode === 'unlink' && (
        <>
          <div className='scope-pills'>
            {SCOPE_OPTIONS.map(option => (
              <button
                key={option.value}
                type='button'
                className={cn('scope-pill', { on: scopeType === option.value })}
                onClick={() => selectScopeType(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
          {scopeType !== 'pin' && (
            <div className='scope-helper'>Only URLs matched within this scope are processed; anything else is skipped and reported.</div>
          )}
        </>
      )}

      {scopeType !== 'pin' && !selected && !isLoadingCard && (
        <div className='scope-search'>
          <input
            type='text'
            value={searchVal}
            placeholder={searchPlaceholder}
            onChange={e => handleSearchChange(e.target.value)}
          />
          <span className='search-hint'>{isSearching ? 'Searching…' : 'search by name or ID'}</span>
          {searchResults && searchResults.length > 0 && (
            <div className='search-results'>
              {searchResults.map(result => (
                <button key={result.id} type='button' className='search-result' onClick={() => selectResult(result)}>
                  {scopeType === 'brand' ? (
                    <>
                      <span className='result-name'>{result.name}</span>
                      <span className='result-meta'>
                        #{result.id} · {(result.productCount || 0).toLocaleString()} products
                        {result.isPartner ? ' · Partner' : ''}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className='result-name'>{result.title}</span>
                      <span className='result-meta'>
                        PID {result.id}
                        {result.brand ? ` · ${result.brand}` : ''}
                      </span>
                    </>
                  )}
                </button>
              ))}
            </div>
          )}
          {showNotFound && <div className='search-not-found'>No match for '{searchVal.trim()}'. Check spelling or search by ID.</div>}
        </div>
      )}

      {isLoadingCard && (
        <div className='scope-card skeleton'>
          <div className='skeleton-bar wide' />
          <div className='skeleton-bar' />
        </div>
      )}

      {selected && !isLoadingCard && (
        <>
          <div className={cn('scope-card', { 'link-accent': mode === 'link' })}>
            <div className='card-main'>
              {selected.type === 'brand' ? (
                <>
                  <div className='card-title'>
                    {selected.name} <span className='card-id'>All Brands ID {selected.id}</span>
                  </div>
                  <div className='card-stats'>
                    {(selected.productCount || 0).toLocaleString()} products · {selected.domainCount || 0} domains · rank{' '}
                    {selected.ranking != null ? `#${selected.ranking}` : '—'} · {selected.isPartner ? 'Partner' : 'Non-partner'}
                  </div>
                </>
              ) : (
                <>
                  <div className='card-title'>
                    <span className='card-id'>PID {selected.id}</span> {selected.title}
                  </div>
                  <div className='card-stats'>
                    {(selected.variantCount || 0).toLocaleString()} variants · {(selected.pinCount || 0).toLocaleString()} pins · rank{' '}
                    {selected.totalRank != null ? `#${selected.totalRank}` : '—'}
                    {selected.brandName ? ` · ${selected.brandName}` : ''}
                  </div>
                </>
              )}
            </div>
            <button type='button' className='card-change' onClick={changeSelection}>
              change
            </button>
          </div>
          {selected.type === 'brand' && selected.similar?.length > 0 && (
            <div className='scope-duplicates'>
              ⚠ Similar records exist:{' '}
              {selected.similar.map(candidate => `${candidate.name} #${candidate.id} (${candidate.productCount} products)`).join(' · ')}
            </div>
          )}
        </>
      )}
    </div>
  );
};

ScopeSelector.propTypes = {
  mode: PropTypes.oneOf(['unlink', 'link']).isRequired,
  scopeType: PropTypes.oneOf(['brand', 'product', 'pin']).isRequired,
  onScopeTypeChange: PropTypes.func.isRequired,
  selected: PropTypes.object,
  onSelect: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired
};

export default ScopeSelector;
